/**
 * The registrar's Identity review queue (cca-yb68, ADR-064).
 *
 * An application whose email matches somebody in CRM but whose details do not
 * ALL agree waits here. The registrar links it to the person already on record
 * (nothing is ever written onto that Contact), creates a new person, or
 * discards it. The old "Confirm & process" — which re-resolved to the first
 * email hit and overwrote that person's date of birth — is gone, along with
 * the guidance that told the registrar to correct the date of birth in Zoho.
 *
 * Designed for a glut of sixty (Miranda §3d): oldest first, collapsed
 * disclosure rows with only the oldest open, a reason filter, an honest
 * "showing N of M" with Load more, and one persistent `role="status"` that is
 * present and empty from the first render (SC 4.1.3).
 */

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, apiPost } from './api';
import { IdentityChooser, type IdentityChoice } from './IdentityChooser';
import {
  COPY,
  DISCARD_CONFIRM,
  applyFilter,
  classifyDiscardError,
  classifyResolveError,
  discardedMessage,
  filterOptions,
  focusAfterResolve,
  hintSentences,
  metaLine,
  parishNameFor,
  rowSummary,
  showingLine,
  siblingNote,
  successMessage,
  waitingPhrase,
  type CandidatesPayload,
  type HeldReasonCode,
  type ReviewItem,
  type ReviewQueueResponse,
} from './identity-review';

interface CandidateState {
  loading: boolean;
  error: string | null;
  payload: CandidatesPayload | null;
}

interface ItemError {
  kind: string;
  message: string;
}

/** Motion is a preference, not a decoration: the jump-to-section scroll and the
 *  flash are gated here as well as in CSS (UX-5, Miranda S3). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function IdentityReviewSection({ onResolved }: { onResolved: () => void }): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [parishes, setParishes] = useState<{ slug: string; name: string }[]>([]);
  const [filter, setFilter] = useState<'all' | HeldReasonCode>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [cands, setCands] = useState<Record<string, CandidateState>>({});
  const [busy, setBusy] = useState<{ id: string; action: 'link' | 'create' | 'discard' } | null>(
    null,
  );
  const [itemErrors, setItemErrors] = useState<Record<string, ItemError>>({});
  const [discardingId, setDiscardingId] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  const headRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const emptyRef = useRef<HTMLParagraphElement | null>(null);
  const deepLinkDone = useRef(false);
  /** Ids whose candidates have been asked for, so opening a card twice (or a
   *  StrictMode double render) never fires a second set of Zoho searches. */
  const requested = useRef<Set<string>>(new Set());

  /** Clearing rebuilds without the key rather than `delete`-ing a computed one
   *  (@typescript-eslint/no-dynamic-delete — the same form the lodgement card
   *  uses). */
  const setItemError = (id: string, err: ItemError | null) =>
    setItemErrors((prev) =>
      err
        ? { ...prev, [id]: err }
        : Object.fromEntries(Object.entries(prev).filter(([key]) => key !== id)),
    );

  const loadCandidates = useCallback(async (id: string) => {
    setCands((prev) => ({ ...prev, [id]: { loading: true, error: null, payload: null } }));
    try {
      const payload = await apiFetch<CandidatesPayload>(
        `/api/vetting/registrar/quarantine/${id}/candidates`,
      );
      setCands((prev) => ({ ...prev, [id]: { loading: false, error: null, payload } }));
    } catch {
      setCands((prev) => ({
        ...prev,
        [id]: { loading: false, error: COPY.candidatesError, payload: null },
      }));
    }
  }, []);

  /** Ask once per item. Candidates are fetched only when a card OPENS —
   *  listing the queue makes no Zoho call at all, which is what keeps a glut
   *  of sixty cheap (at most 6 searches per opened card). */
  const requestCandidates = useCallback(
    (id: string) => {
      if (requested.current.has(id)) return;
      requested.current.add(id);
      void loadCandidates(id);
    },
    [loadCandidates],
  );

  const loadQueue = useCallback(
    async (cursor?: string) => {
      if (cursor) setLoadingMore(true);
      try {
        const path = cursor
          ? `/api/vetting/registrar/match-queue?cursor=${encodeURIComponent(cursor)}`
          : '/api/vetting/registrar/match-queue';
        const d = await apiFetch<ReviewQueueResponse>(path);
        setItems((prev) => (cursor ? [...prev, ...d.items] : d.items));
        setTotal(d.total);
        setNextCursor(d.hasMore ? d.nextCursor : null);
        setLoadError(null);
        // Oldest first: only the oldest row starts open — sixty open cards
        // would be about 24,000px of scrolling.
        if (!cursor) {
          const first = d.items[0];
          setOpenId(first?.id ?? null);
          if (first) requestCandidates(first.id);
        }
      } catch {
        setLoadError(COPY.loadError);
      } finally {
        setLoaded(true);
        setLoadingMore(false);
      }
    },
    [requestCandidates],
  );

  useEffect(() => {
    void loadQueue();
    apiFetch<{ parishes: { slug: string; name: string }[] }>('/api/vetting/registrar/parishes')
      .then((d) => setParishes(d.parishes))
      .catch(() => {
        /* non-fatal: rows fall back to the slug */
      });
  }, [loadQueue]);

  const openItem = useCallback(
    (id: string) => {
      setOpenId(id);
      setDiscardingId(null);
      requestCandidates(id);
    },
    [requestCandidates],
  );

  /** The registrar's alert email deep-links one item: open it and focus it. */
  useEffect(() => {
    if (deepLinkDone.current || !loaded) return;
    const wanted = searchParams.get('review');
    if (!wanted) {
      deepLinkDone.current = true;
      return;
    }
    if (!items.some((i) => i.id === wanted)) return;
    deepLinkDone.current = true;
    setFilter('all');
    openItem(wanted);
    window.setTimeout(() => {
      const el = headRefs.current[wanted];
      el?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      el?.focus();
    }, 0);
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
  }, [items, loaded, openItem, searchParams, setSearchParams]);

  // Clearing the last item of a reason would otherwise leave the registrar
  // looking at an empty list under a filter that no longer exists.
  useEffect(() => {
    if (filter !== 'all' && !items.some((i) => i.reasonCode === filter)) setFilter('all');
  }, [filter, items]);

  function removeItem(id: string) {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setTotal((t) => Math.max(0, t - 1));
  }

  function moveFocusAfter(visible: ReviewItem[], resolvedId: string) {
    const target = focusAfterResolve(visible, resolvedId);
    window.setTimeout(() => {
      if (target.kind === 'empty') {
        emptyRef.current?.focus();
        return;
      }
      // The next item OPENS as focus lands on it, so the registrar carries
      // straight on without a second keystroke.
      openItem(target.id);
      headRefs.current[target.id]?.focus();
    }, 0);
  }

  async function resolve(item: ReviewItem, choice: IdentityChoice, crmName: string | null) {
    setBusy({ id: item.id, action: choice.action });
    setItemError(item.id, null);
    const visible = applyFilter(items, filter);
    const parishName = parishNameFor(item.parishSlug, parishes);
    try {
      await apiPost(`/api/vetting/registrar/quarantine/${item.id}/resolve`, {
        action: choice.action,
        ...(choice.contactId ? { contactId: choice.contactId } : {}),
        version: choice.version,
      });
      // A repeat of a choice that already succeeded answers 200 with the stored
      // outcome (`repeat: true`) — a success, not an error.
      removeItem(item.id);
      setStatus(
        successMessage({
          action: choice.action,
          typedName: item.applicantName,
          crmName,
          parishName,
          remaining: Math.max(0, total - 1),
        }),
      );
      moveFocusAfter(visible, item.id);
      onResolved();
    } catch (err) {
      const failure = classifyResolveError(err, choice.action);
      if (failure.kind === 'already_resolved') {
        removeItem(item.id);
        setStatus(failure.message);
        moveFocusAfter(visible, item.id);
        onResolved();
      } else if (failure.kind === 'record_changed' && failure.fresh) {
        // The candidate set moved: show the CURRENT people, and let the
        // registrar choose again. The stale choice is dropped, never resent.
        setCands((prev) => ({
          ...prev,
          [item.id]: { loading: false, error: null, payload: failure.fresh ?? null },
        }));
        setItemError(item.id, failure);
      } else {
        setItemError(item.id, failure);
      }
    } finally {
      setBusy(null);
    }
  }

  async function discard(item: ReviewItem) {
    setBusy({ id: item.id, action: 'discard' });
    setItemError(item.id, null);
    const visible = applyFilter(items, filter);
    try {
      await apiPost(`/api/vetting/registrar/quarantine/${item.id}/discard`, {});
      setDiscardingId(null);
      removeItem(item.id);
      setStatus(discardedMessage(item.applicantName, Math.max(0, total - 1)));
      moveFocusAfter(visible, item.id);
      onResolved();
    } catch (err) {
      const failure = classifyDiscardError(err);
      if (failure.kind === 'already_resolved') {
        setDiscardingId(null);
        removeItem(item.id);
        setStatus(failure.message);
        moveFocusAfter(visible, item.id);
        onResolved();
      } else {
        setItemError(item.id, failure);
      }
    } finally {
      setBusy(null);
    }
  }

  const visible = applyFilter(items, filter);
  const options = filterOptions(items, total);
  const capLine = showingLine(items.length, total);
  const oldest = items[0]?.createdAt ?? null;

  return (
    // `#identity-review` is the fragment the registrar's alert email links to.
    <section id="identity-review" className="idr-section" aria-labelledby="idr-heading">
      <div className="idr-head">
        <h2 id="idr-heading">{COPY.heading}</h2>
        {total > 0 && <p className="idr-meta">{metaLine(total, oldest)}</p>}
      </div>

      {items.length > 0 && (
        <div className="idr-filter">
          <label htmlFor="idr-filter-select">{COPY.filterLabel}</label>
          <select
            id="idr-filter-select"
            className="select"
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | HeldReasonCode)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {capLine && <p className="idr-note">{capLine}</p>}

      {/* Present and empty from the first render, so an action is announced. */}
      <p className="idr-status" role="status" aria-live="polite">
        {status}
      </p>

      {!loaded && <p className="idr-note">{COPY.loading}</p>}

      {loadError && (
        <div>
          <p className="idr-error" role="alert">
            {loadError}
          </p>
          <button type="button" className="btn btn-ghost" onClick={() => void loadQueue()}>
            {COPY.tryAgain}
          </button>
        </div>
      )}

      {loaded && !loadError && visible.length === 0 && (
        <p className="idr-empty" tabIndex={-1} ref={emptyRef}>
          {COPY.empty}
        </p>
      )}

      {visible.map((item) => {
        const open = openId === item.id;
        const parishName = parishNameFor(item.parishSlug, parishes);
        const state = cands[item.id];
        const err = itemErrors[item.id];
        const sibling = siblingNote(items, item);
        const subjectName =
          state?.payload && state.payload.candidates.length === 1
            ? (state.payload.candidates[0]?.name ?? null)
            : null;
        return (
          <article
            className="idr-item"
            key={item.id}
            aria-busy={busy !== null && busy.id === item.id}
          >
            <h3 className="idr-item-head">
              <button
                type="button"
                aria-expanded={open}
                aria-controls={`idr-panel-${item.id}`}
                aria-label={rowSummary(item, parishName)}
                ref={(el) => {
                  headRefs.current[item.id] = el;
                }}
                onClick={() => (open ? setOpenId(null) : openItem(item.id))}
              >
                <span aria-hidden="true" className="idr-caret">
                  {open ? '▾' : '▸'}
                </span>
                {/* The accessible name is the aria-label above, in Miranda's
                    exact order; these spans are the same facts, laid out. */}
                <span className="idr-item-name">{item.applicantName}</span>
                <span className="idr-item-sub">
                  {parishName} · {item.reasonLabel} · {waitingPhrase(item.createdAt)}
                  {sibling ? ` · ${sibling}` : ''}
                </span>
              </button>
            </h3>

            {open && (
              <div className="idr-card" id={`idr-panel-${item.id}`}>
                {item.legacy && <p className="idr-note">{COPY.legacyNote}</p>}
                {/* The queue row carries the hint codes recorded when the
                    application was held, so why it is here can be read before
                    the CRM lookup answers — or if it never does. */}
                {!state?.payload && item.hints.length > 0 && (
                  <ul className="idr-hints">
                    {hintSentences(item.hints).map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                )}
                <IdentityChooser
                  typed={item}
                  parishName={parishName}
                  payload={state?.payload ?? null}
                  loading={state?.loading ?? true}
                  loadError={state?.error ?? null}
                  onRetryLoad={() => void loadCandidates(item.id)}
                  busy={
                    busy !== null && busy.id === item.id && busy.action !== 'discard'
                      ? busy.action
                      : null
                  }
                  error={err?.message ?? null}
                  onChoose={(choice) => {
                    const name =
                      choice.action === 'link'
                        ? (state?.payload?.candidates.find((c) => c.contactId === choice.contactId)
                            ?.name ?? subjectName)
                        : null;
                    void resolve(item, choice, name);
                  }}
                  footer={
                    discardingId === item.id ? (
                      <div className="idr-confirm">
                        <p className="idr-confirm-head">{DISCARD_CONFIRM.heading}</p>
                        <p className="idr-confirm-body">{DISCARD_CONFIRM.body}</p>
                        <div className="idr-actions">
                          <button
                            type="button"
                            className="btn btn-ghost idr-danger"
                            aria-disabled={busy !== null}
                            onClick={() => {
                              if (busy === null) void discard(item);
                            }}
                          >
                            {busy !== null && busy.id === item.id && busy.action === 'discard'
                              ? 'Discarding…'
                              : DISCARD_CONFIRM.confirm}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setDiscardingId(null)}
                          >
                            {DISCARD_CONFIRM.back}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="idr-footer-row">
                        <a
                          className="idr-link"
                          href={`mailto:${item.applicantEmail}?subject=${encodeURIComponent('Your CCAANZ police vetting application')}`}
                        >
                          {COPY.emailApplicant}
                        </a>
                        <button
                          type="button"
                          className="idr-link idr-danger"
                          onClick={() => setDiscardingId(item.id)}
                        >
                          {COPY.discard}
                        </button>
                        <span className="idr-received">{COPY.received(item.createdAt)}</span>
                      </div>
                    )
                  }
                />
                {err?.kind === 'in_progress' && (
                  <button type="button" className="btn btn-ghost" onClick={() => void loadQueue()}>
                    {COPY.reloadList}
                  </button>
                )}
              </div>
            )}
          </article>
        );
      })}

      {nextCursor && (
        <button
          type="button"
          className="btn btn-ghost idr-more"
          disabled={loadingMore}
          onClick={() => void loadQueue(nextCursor)}
        >
          {loadingMore ? COPY.loadingMore : COPY.loadMore}
        </button>
      )}
    </section>
  );
}
